/*This component is specific to personskade claims*/
import React, { useState, useCallback, useContext, useEffect } from 'react';
import { format } from 'date-fns';
import { RadioField, RadioInput, Button, Box, SelectField, DateField, TextField } from '@flos/react-ui';
import { ElementValue } from '../../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../../helpers/templates';
import useAppState from '../../../state/appState';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { ElementValueHuman } from '../../../types/element-values';
import { getValue } from '../../values/Get';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getDateObject } from '../../fields/Datepicker';
import { fieldLabel } from './utils';
import { ConfigContext } from '../../../context/ConfigContext';
import { HospitalList } from '../../../types/services';
import { getHospitalList } from '../../../services/hospitals';

type FieldSet = {
    specificInvestigatedPreviouslyWhere: {
        label: string;
        value: string;
    };
    specificInvestigatedPreviouslyTreatmentName: {
        label: string;
        value: string;
    };
    specificInvestigatedPreviouslyTreatmentDate: {
        label: string;
        value: string;
    };
    specificInvestigatedPreviouslyTreatmentAddress: {
        label: string;
        value: string;
    };
};

type ExaminationValue = {
    fieldSets: FieldSet[];
};

const PreviousExamination = ({ elementId, caption }: any) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const topConfig = useContext(ConfigContext);
    const [hospitals, setHospitals] = useState<HospitalList>([]);

    useEffect(() => {
        const fetchHospitals = async () => {
            const response = await getHospitalList(topConfig);
            if (response) {
                setHospitals(response);
            }
        };
        fetchHospitals();
    }, [topConfig]);

    // Dropdown options for practitioner types
    const values: any = [
        { caption: 'Hospital', value: 'HOSPITAL' },
        { caption: 'Egen læge', value: 'DOCTOR' },
        { caption: 'Kiropraktor', value: 'CHIROPRACTOR' },
        { caption: 'Fysioterapeut', value: 'PHYSIOTHERAPIST' },
        { caption: 'Andet', value: 'OTHER' }
    ];

    // Initialize field sets from form values/default values
    const initializeFieldSets = useCallback(() => {
        if (formValues && formValues[elementId] && formValues[elementId].fieldSets) {
            return formValues[elementId].fieldSets;
        }
        return [
            {
                specificInvestigatedPreviouslyWhere: { label: '', value: '' },
                specificInvestigatedPreviouslyTreatmentName: { label: '', value: '' },
                specificInvestigatedPreviouslyTreatmentDate: { label: '', value: '' },
                specificInvestigatedPreviouslyTreatmentAddress: { label: '', value: '' }
            }
        ];
    }, [formValues, elementId]);

    const [fieldSets, setFieldSets] = useState(initializeFieldSets);
    const [isVisible, setIsVisible] = useState(
        fieldSets?.length >= 1 && fieldSets[0].specificInvestigatedPreviouslyWhere.value !== ''
    );

    // Update form values
    const updateFormValues = (updatedFieldSets: any) => {
        setFieldSets(updatedFieldSets);
        setFormValue(elementId, { fieldSets: updatedFieldSets });
    };

    // Add a new field set
    const addItem = () => {
        if (!isVisible) {
            setIsVisible(true);
        } else {
            const newFieldSets = [
                ...fieldSets,
                {
                    specificInvestigatedPreviouslyWhere: { label: '', value: '' },
                    specificInvestigatedPreviouslyTreatmentName: { label: '', value: '' },
                    specificInvestigatedPreviouslyTreatmentDate: { label: '', value: '' },
                    specificInvestigatedPreviouslyTreatmentAddress: { label: '', value: '' }
                }
            ];
            updateFormValues(newFieldSets);
        }
    };

    // Remove a field set by index
    const removeItem = (index: number) => {
        const updatedFieldSets = fieldSets.filter((_: any, i: any) => i !== index);
        updateFormValues(updatedFieldSets);
    };

    // Handle field changes within a field set
    const handleFieldChange = (index: number, field: string, value: any) => {
        const updatedFieldSets = fieldSets.map((set: any, i: any) => (i === index ? { ...set, [field]: value } : set));
        updateFormValues(updatedFieldSets);
    };

    return (
        <fieldset>
            {isVisible &&
                fieldSets.map((fieldSet: FieldSet, index: number) => (
                    <div className="u-spacing-stack-m">
                        <Box theme="secondary" key={index}>
                            <RadioField
                                label={fieldLabel.specificInvestigatedPreviouslyWhere}
                                id={`specificInvestigatedPreviouslyWhere-${index}`}
                                key={`specificInvestigatedPreviouslyWhere-${index}`}
                                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = evt.target.value;
                                    const label = values.find((item: any) => item.value === value)?.caption;
                                    handleFieldChange(index, 'specificInvestigatedPreviouslyWhere', { label, value });
                                }}
                                value={fieldSet.specificInvestigatedPreviouslyWhere.value}
                                required
                            >
                                {values.map((option: any, key: any) => (
                                    <RadioInput key={key} label={option.caption} value={option.value} />
                                ))}
                            </RadioField>
                            <div className="u-spacing-inset-m--stretch-max">
                                {fieldSet.specificInvestigatedPreviouslyWhere.value === 'HOSPITAL' && (
                                    <>
                                        <SelectField
                                            label={fieldLabel.bodyPractitionerHospitalName}
                                            id={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                                            key={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                                            options={hospitals}
                                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                                const value = event.target.value;
                                                handleFieldChange(
                                                    index,
                                                    'specificInvestigatedPreviouslyTreatmentName',
                                                    {
                                                        label: fieldLabel.bodyPractitionerHospitalName,
                                                        value
                                                    }
                                                );
                                            }}
                                            value={fieldSet.specificInvestigatedPreviouslyTreatmentName.value}
                                        />
                                    </>
                                )}
                                {fieldSet.specificInvestigatedPreviouslyWhere.value &&
                                    fieldSet.specificInvestigatedPreviouslyWhere.value !== 'HOSPITAL' && (
                                        <>
                                            <TextField
                                                required
                                                value={fieldSet.specificInvestigatedPreviouslyTreatmentName.value}
                                                id={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                                                key={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                                                label={fieldLabel.specificInvestigatedPreviouslyTreatmentName}
                                                onChange={({ target: { value } }) => {
                                                    handleFieldChange(
                                                        index,
                                                        'specificInvestigatedPreviouslyTreatmentName',
                                                        {
                                                            label: fieldLabel.specificInvestigatedPreviouslyTreatmentName,
                                                            value
                                                        }
                                                    );
                                                }}
                                            />
                                            <TextField
                                                required
                                                value={fieldSet.specificInvestigatedPreviouslyTreatmentAddress.value}
                                                id={`specificInvestigatedPreviouslyTreatmentAddress-${index}`}
                                                key={`specificInvestigatedPreviouslyTreatmentAddress-${index}`}
                                                label={fieldLabel.specificInvestigatedPreviouslyTreatmentAddress}
                                                onChange={({ target: { value } }) => {
                                                    handleFieldChange(
                                                        index,
                                                        'specificInvestigatedPreviouslyTreatmentAddress',
                                                        {
                                                            label: fieldLabel.specificInvestigatedPreviouslyTreatmentAddress,
                                                            value
                                                        }
                                                    );
                                                }}
                                            />
                                        </>
                                    )}
                                {fieldSet.specificInvestigatedPreviouslyWhere.value && (
                                    <DateField
                                        dateFormat="dd-MM-yyyy"
                                        selected={getDateObject(
                                            fieldSet.specificInvestigatedPreviouslyTreatmentDate.value
                                        )}
                                        onChange={(date: Date | null) => {
                                            if (!date) return;
                                            handleFieldChange(index, 'specificInvestigatedPreviouslyTreatmentDate', {
                                                label: fieldLabel.specificInvestigatedPreviouslyTreatmentDate,
                                                value: format(date, 'dd-MM-yyyy')
                                            });
                                        }}
                                        onFocus={(e: any) => e.target.blur()} // Prevent keyboard on mobile - https://stackoverflow.com/a/69217777
                                        id={`specificInvestigatedPreviouslyTreatmentDate-${index}`}
                                        key={`specificInvestigatedPreviouslyTreatmentDate-${index}`}
                                        errorText="Vælg dato"
                                        label={fieldLabel.specificInvestigatedPreviouslyTreatmentDate}
                                        required
                                        aria-label={caption}
                                        data-testid="datefield"
                                        maxDate={new Date()}
                                        minDate={new Date(`1970-01-01`)}
                                        wrapperClassName="u-spacing-stack-none"
                                    />
                                )}
                            </div>
                            <Button theme="secondary" onClick={() => removeItem(index)}>
                                {' '}
                                Fjern
                            </Button>
                        </Box>
                    </div>
                ))}

            <Button onClick={addItem}>Tilføj ny undersøgelse</Button>
        </fieldset>
    );
};

PreviousExamination.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const value: ExaminationValue = getValue(formConfig, formValues, elementId);
    if (value === undefined) {
        return <></>;
    }

    const contentSummaryRowTemplate: JSX.Element[] = [];
    const { fieldSets } = value;

    fieldSets?.forEach((fieldSet: FieldSet, index: number) => {
        const {
            specificInvestigatedPreviouslyWhere,
            specificInvestigatedPreviouslyTreatmentName,
            specificInvestigatedPreviouslyTreatmentDate,
            specificInvestigatedPreviouslyTreatmentAddress
        } = fieldSet;

        if (specificInvestigatedPreviouslyWhere.value === 'HOSPITAL') {
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyWhere-${index}`}
                    elementId={elementId}
                    caption={fieldLabel.specificInvestigatedPreviouslyWhere}
                    value={specificInvestigatedPreviouslyWhere.label}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                    elementId={elementId}
                    caption={specificInvestigatedPreviouslyTreatmentName.label}
                    value={specificInvestigatedPreviouslyTreatmentName.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyTreatmentDate-${index}`}
                    elementId={elementId}
                    caption={specificInvestigatedPreviouslyTreatmentDate.label}
                    value={specificInvestigatedPreviouslyTreatmentDate.value}
                />
            );
        } else {
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyWhere-${index}`}
                    elementId={elementId}
                    caption={fieldLabel.specificInvestigatedPreviouslyWhere}
                    value={specificInvestigatedPreviouslyWhere.label}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyTreatmentName-${index}`}
                    elementId={elementId}
                    caption={specificInvestigatedPreviouslyTreatmentName.label}
                    value={specificInvestigatedPreviouslyTreatmentName.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyTreatmentAddress-${index}`}
                    elementId={elementId}
                    caption={specificInvestigatedPreviouslyTreatmentAddress.label}
                    value={specificInvestigatedPreviouslyTreatmentAddress.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`specificInvestigatedPreviouslyTreatmentDate-${index}`}
                    elementId={elementId}
                    caption={specificInvestigatedPreviouslyTreatmentDate.label}
                    value={specificInvestigatedPreviouslyTreatmentDate.value}
                />
            );
        }
    });

    return <>{contentSummaryRowTemplate}</>;
};

PreviousExamination.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue | ClaimFormSubmitFieldValue[] | undefined => {
    const submitValue: ClaimFormSubmitFieldValue[] = [];
    const valueObj = PreviousExamination.getValue(formValues, elementId);
    const { fieldSets } = valueObj;

    fieldSets?.forEach((fieldSet: any, index: any) => {
        if (fieldSet.specificInvestigatedPreviouslyWhere.value) {
            const specificInvestigatedPreviouslyWhere = getClaimFormSubmitValue(
                'radiobuttons',
                `specificInvestigatedPreviouslyWhere-${index}`,
                fieldLabel.specificInvestigatedPreviouslyWhere,
                fieldSet.specificInvestigatedPreviouslyWhere.label,
                { string: fieldSet.specificInvestigatedPreviouslyWhere.value }
            );
            submitValue.push(specificInvestigatedPreviouslyWhere);
        }
        if (fieldSet.specificInvestigatedPreviouslyTreatmentName.value) {
            const specificInvestigatedPreviouslyTreatmentName = getClaimFormSubmitValue(
                'dropdown',
                `specificInvestigatedPreviouslyWhere-${index}`,
                fieldSet.specificInvestigatedPreviouslyTreatmentName.label,
                fieldSet.specificInvestigatedPreviouslyTreatmentName.value,
                { string: fieldSet.specificInvestigatedPreviouslyTreatmentName.value }
            );
            submitValue.push(specificInvestigatedPreviouslyTreatmentName);
        }
        if (fieldSet.specificInvestigatedPreviouslyTreatmentAddress.value) {
            const specificInvestigatedPreviouslyTreatmentDate = getClaimFormSubmitValue(
                'textfield',
                `specificInvestigatedPreviouslyTreatmentAddress-${index}`,
                fieldSet.specificInvestigatedPreviouslyTreatmentAddress.label,
                fieldSet.specificInvestigatedPreviouslyTreatmentAddress.value,
                { string: fieldSet.specificInvestigatedPreviouslyTreatmentAddress.value }
            );
            submitValue.push(specificInvestigatedPreviouslyTreatmentDate);
        }
        if (fieldSet.specificInvestigatedPreviouslyTreatmentDate.value) {
            const specificInvestigatedPreviouslyTreatmentDate = getClaimFormSubmitValue(
                'datepicker',
                `specificInvestigatedPreviouslyTreatmentDate-${index}`,
                fieldSet.specificInvestigatedPreviouslyTreatmentDate.label,
                fieldSet.specificInvestigatedPreviouslyTreatmentDate.value,
                { string: fieldSet.specificInvestigatedPreviouslyTreatmentDate.value }
            );
            submitValue.push(specificInvestigatedPreviouslyTreatmentDate);
        }
    });

    return submitValue;
};

PreviousExamination.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

PreviousExamination.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = PreviousExamination.getValue(formValues, elementId);
    return value === undefined ? '' : value;
};

PreviousExamination.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    return PreviousExamination.getValue(formValues, elementId);
};

// This returns the component's isValid status
PreviousExamination.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const valueObj = PreviousExamination.getValue(formValues, elementId);

    if (!valueObj) {
        return false;
    }

    const { fieldSets } = valueObj;

    if (!fieldSets) {
        return true;
    }

    return fieldSets.every((fieldSet: any) => {
        if (fieldSet.specificInvestigatedPreviouslyWhere.value === 'HOSPITAL') {
            return (
                fieldSet.specificInvestigatedPreviouslyTreatmentName.value &&
                fieldSet.specificInvestigatedPreviouslyTreatmentDate.value
            );
        } else {
            return (
                fieldSet.specificInvestigatedPreviouslyWhere.value &&
                fieldSet.specificInvestigatedPreviouslyTreatmentName.value &&
                fieldSet.specificInvestigatedPreviouslyTreatmentAddress.value &&
                fieldSet.specificInvestigatedPreviouslyTreatmentDate.value
            );
        }
    });
};

PreviousExamination.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig || !fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    return PreviousExamination.isValid(formConfig, formValues, elementId);
};

export default PreviousExamination;
